<template lang="pug">
  //- UC-REG-200 — El FAB flotante se sacó. Ahora se abre desde el ítem
  //- "Asistente" del SidebarNav, que emite $bus.$emit('openSupportChat').
  //- El widget solo renderiza el panel cuando está abierto.
  .support-chat-widget(v-if="visible && isOpen")
    .support-panel
      header.support-panel-head
        .support-panel-head-title
          .support-panel-status-dot(:class="statusDotClass")
          .support-panel-head-text
            .support-panel-head-name Asistente
            .support-panel-head-sub {{ headerSubtitle }}
        button.support-panel-close(
          type="button"
          aria-label="Cerrar"
          @click="isOpen = false"
        )
          micon(name="clear")

      .support-panel-thread(ref="messagesScroll")
        template(v-for="m in messages")
          .support-msg(
            :key="m._id"
            :class="messageClass(m)"
          )
            .support-msg-bubble
              .support-msg-text {{ m.text }}

          //- CSAT chips: solo debajo de la última respuesta del bot si no se votó.
          .support-feedback(
            v-if="shouldShowFeedbackAfter(m)"
            :key="`fb-${m._id}`"
          )
            span.support-feedback-label ¿Te sirvió?
            button.support-feedback-chip(type="button" @click="sendFeedback('positive')")
              | Sí
            button.support-feedback-chip(type="button" @click="sendFeedback('negative')")
              | No

        //- Typing animado (3 dots).
        .support-msg.is-bot(v-if="isSending")
          .support-msg-bubble.is-typing
            span.dot
            span.dot
            span.dot

        //- Card de derivación a humano — visible solo cuando:
        //-   - el user marcó 👎 (negativeAt set), o
        //-   - el bot escaló (status='waiting_human').
        //- Si ya estamos esperando humano o atendidos, no mostrar la card.
        .support-handoff-card(v-if="shouldShowHandoff")
          .support-handoff-text {{ handoffMessage }}
          button.support-handoff-btn(
            v-if="!isWaitingOrHandling"
            type="button"
            @click="escalateNow"
          ) Sí, derivame con una persona

      .support-panel-input(v-if="canType")
        textarea.support-panel-textarea(
          v-model="draft"
          placeholder="Escribí tu mensaje"
          rows="1"
          :disabled="isSending"
          @keydown.enter.exact.prevent="sendMessage"
        )
        button.support-panel-send(
          type="button"
          aria-label="Enviar"
          :disabled="!draft.trim() || isSending"
          @click="sendMessage"
        )
          micon(name="chevron_right")

      .support-panel-closed(v-else)
        button.support-panel-reopen(type="button" @click="reopen")
          | Iniciar nueva conversación
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportChatWidget
 * @description Widget flotante del bot de soporte (UC-REG-098 + rediseño
 *              UC-REG-XXX). FAB navy con icono del sprite + panel
 *              minimalista. Captura contexto al primer mensaje, polling
 *              3s, CSAT chips inline, card de derivación a humano solo
 *              después de 👎 o escalación (no link fijo).
 *
 *              Visible solo si el tenant tiene `support.assistant` ON y
 *              el rol del user != support.
 *
 * @example <SupportChatWidget />
 */
export default {
  name: "SupportChatWidget",
  data() {
    return {
      visible: false,
      isOpen: false,
      isSending: false,
      conversationId: null,
      messages: [],
      status: "bot_handling",
      assignedTo: null,
      draft: "",
      pollTimer: null,
      lastMessageId: null,
      hasFeedback: false,
      negativeAt: null,
    };
  },
  computed: {
    /**
     * @description Clase del puntito de estado.
     * @returns {string}
     */
    statusDotClass() {
      if (this.status === "human_handling") return "is-blue";
      if (this.status === "waiting_human") return "is-orange";
      if (this.status === "resolved") return "is-grey";
      return "is-green";
    },
    /**
     * @description Subtítulo del header según estado.
     * @returns {string}
     */
    headerSubtitle() {
      if (this.status === "waiting_human") return "te derivamos con soporte";
      if (this.status === "human_handling")
        return `${this.assignedToName} te atiende`;
      if (this.status === "resolved") return "conversación cerrada";
      return "responde en segundos";
    },
    /**
     * @description Nombre del operador humano asignado.
     * @returns {string}
     */
    assignedToName() {
      if (!this.assignedTo) return "Soporte";
      const u = this.assignedTo;
      return (
        `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
        u.username ||
        "Soporte"
      );
    },
    /**
     * @description Mensaje de la card de derivación según el contexto.
     * @returns {string}
     */
    handoffMessage() {
      if (this.status === "waiting_human") {
        return "Ya te derivamos con soporte humano. Te respondemos en breve.";
      }
      return "¿Querés que te derivemos con una persona del equipo de soporte?";
    },
    /**
     * @description True si se debe mostrar la card de derivación.
     *              Reglas: bot escaló (status=waiting_human) O user marcó 👎.
     * @returns {boolean}
     */
    shouldShowHandoff() {
      if (this.status === "waiting_human") return true;
      if (this.negativeAt) return true;
      return false;
    },
    /**
     * @description True si la conversación ya está en manos de un humano.
     * @returns {boolean}
     */
    isWaitingOrHandling() {
      return (
        this.status === "waiting_human" || this.status === "human_handling"
      );
    },
    /**
     * @description True si el textarea está habilitado.
     * @returns {boolean}
     */
    canType() {
      return this.status !== "resolved";
    },
    /**
     * @description Índice del último mensaje del bot.
     * @returns {number}
     */
    lastBotMessageIdx() {
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        if (this.messages[i].sender === "bot") return i;
      }
      return -1;
    },
  },
  mounted() {
    this.checkVisibility();
    const persisted = localStorage.getItem("supportConversationId");
    if (persisted) {
      this.conversationId = persisted;
    }
    // UC-REG-200 — El chat ahora se abre desde el ítem "Asistente" del
    // SidebarNav, que emite este evento en $bus (o $root si no hay bus).
    const bus = this.$bus || this.$root;
    if (bus && bus.$on) bus.$on("openSupportChat", this.openChat);
  },
  beforeDestroy() {
    this.stopPolling();
    const bus = this.$bus || this.$root;
    if (bus && bus.$off) bus.$off("openSupportChat", this.openChat);
  },
  methods: {
    /**
     * @description Lee user del store y decide si se monta el widget.
     *              Excluye `patient` (tienen portal aparte) y `support`
     *              (son los que atienden las escaladas). El bot apunta al
     *              personal del consultorio (médicos, secretarias, admin,
     *              manager) — su KB está poblada con `audiencia: doctor +
     *              secretary` pero el filtro por audiencia es laxo, así
     *              que admin/manager también pueden consultar.
     *
     *              Feature flag: usa `support.module` (mismo que el grupo
     *              Soporte del sidebar). Si la org no usa soporte, off TODO
     *              — sidebar group y FAB widget. Federico 2026-06-26 (CNC).
     *              Default ON: ausencia del flag = visible.
     */
    checkVisibility() {
      const user = this.$store && this.$store.state && this.$store.state.user;
      if (!user) {
        this.visible = false;
        return;
      }
      if (user.role === "support" || user.role === "patient") {
        this.visible = false;
        return;
      }
      const oc =
        this.$store.state.organizationContext || user.organizationContext || {};
      const features =
        oc.features instanceof Map
          ? Object.fromEntries(oc.features.entries())
          : oc.features || {};
      const supportModule =
        features["support.module"] ??
        (features.support && features.support.module);
      if (supportModule === false) {
        this.visible = false;
        return;
      }
      this.visible = true;
    },
    /**
     * @description Abre el panel y carga la conversación. Si NO existe,
     *              crea una nueva para que el saludo del bot aparezca
     *              inmediato — sin esto, el saludo solo se generaba en el
     *              primer sendMessage y el user veía un panel vacío con
     *              un input que decía "Escribí tu mensaje" sin contexto
     *              (UX feedback de Federico 2026-06-11).
     */
    async openChat() {
      this.isOpen = true;
      if (this.conversationId) {
        await this.loadConversation();
        this.startPolling();
      } else {
        try {
          await this.ensureConversation();
        } catch (err) {
          // Si falla la creación al abrir, no rompemos la UX — el primer
          // sendMessage va a re-intentar ensureConversation y mostrar el
          // toast si hace falta.
        }
      }
    },
    /**
     * @description Carga conversación existente desde el backend.
     */
    async loadConversation() {
      try {
        const data = await supportService.getConversation(this.conversationId);
        this.messages = data.messages || [];
        this.status = data.conversation.status;
        this.assignedTo = data.conversation.assignedTo;
        if (this.messages.length) {
          this.lastMessageId = this.messages[this.messages.length - 1]._id;
        }
        this.scrollDown();
      } catch (err) {
        localStorage.removeItem("supportConversationId");
        this.conversationId = null;
        this.messages = [];
      }
    },
    /**
     * @description Crea conversación nueva en el primer mensaje.
     */
    async ensureConversation() {
      if (this.conversationId) return;
      const ctx = this.captureContext();
      const conv = await supportService.startConversation(ctx);
      this.conversationId = conv._id;
      localStorage.setItem("supportConversationId", conv._id);
      const detail = await supportService.getConversation(conv._id);
      this.messages = detail.messages;
      this.status = detail.conversation.status;
      if (this.messages.length) {
        this.lastMessageId = this.messages[this.messages.length - 1]._id;
      }
      this.startPolling();
    },
    /**
     * @description Captura contexto del entorno para el bot.
     * @returns {Object}
     */
    captureContext() {
      return {
        route: this.$route ? this.$route.path : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        lastErrors: (window.__quantuxErrorBuffer || []).slice(-3),
        moduleHint: this.inferModuleHint(),
        patientId:
          (this.$route && this.$route.params && this.$route.params.id) || null,
      };
    },
    /**
     * @description Infiere qué módulo del producto está abierto.
     * @returns {string}
     */
    inferModuleHint() {
      const p = (this.$route && this.$route.path) || "";
      if (p.includes("/prescription")) return "receta-electronica";
      if (p.includes("/patient")) return "historia-clinica";
      if (p.includes("/appointment")) return "agenda";
      if (p.includes("/validation")) return "validacion";
      return "";
    },
    /**
     * @description Manda mensaje del usuario. Renderiza optimísticamente
     *              el texto del user ANTES de esperar al backend para que
     *              el user vea su mensaje en pantalla mientras el bot
     *              piensa — sin esto, el user mandaba un mensaje y veía
     *              el chat congelado hasta que llegara la respuesta del
     *              bot (UX feedback de Federico 2026-06-11). Si el backend
     *              devuelve el `userMsg` real, reemplazamos el optimístico
     *              en su lugar; si falla, lo removemos.
     */
    async sendMessage() {
      const text = this.draft.trim();
      if (!text || this.isSending) return;
      this.isSending = true;
      this.draft = "";

      // Optimistic UI: pushear el mensaje del user al toque para que
      // aparezca en la pantalla mientras esperamos el backend.
      const tempId = `temp-${Date.now()}`;
      this.messages.push({
        _id: tempId,
        sender: "user",
        text,
        sentAt: new Date(),
        _optimistic: true,
      });
      this.scrollDown();

      try {
        await this.ensureConversation();
        const result = await supportService.sendUserMessage(
          this.conversationId,
          text
        );
        // Reemplazar el optimistic temp por el userMsg real del backend.
        // Si el polling ya trajo el userMsg real mientras esperábamos al
        // LLM, mergeMessages lo dedupea por _id (UC-REG-114).
        const tempIdx = this.messages.findIndex((m) => m._id === tempId);
        if (tempIdx >= 0) this.messages.splice(tempIdx, 1);
        if (result.userMsg) this.mergeMessages([result.userMsg]);
        if (result.botMsg) this.mergeMessages([result.botMsg]);
        if (result.escalated) this.status = "waiting_human";
        if (this.messages.length) {
          this.lastMessageId = this.messages[this.messages.length - 1]._id;
        }
        this.scrollDown();
      } catch (err) {
        // Backend falló — sacar el mensaje optimístico para que el user
        // no piense que se mandó OK.
        const tempIdx = this.messages.findIndex((m) => m._id === tempId);
        if (tempIdx >= 0) this.messages.splice(tempIdx, 1);
        this.handleBackendError(err);
      } finally {
        this.isSending = false;
      }
    },
    /**
     * @description Mapea errores del backend del bot a mensajes user-friendly.
     *              Para 503/500 con códigos conocidos, muestra un mensaje
     *              específico. Para 404 ConversationNotFound, limpia el
     *              localStorage y reinicia (la convo persistida ya no existe).
     *              Para otros, muestra el message del backend o un genérico.
     * @param {Error} err - error de axios
     */
    handleBackendError(err) {
      const status = (err.response && err.response.status) || 0;
      const data = (err.response && err.response.data) || {};
      const code = data.message || data.error || "";

      if (status === 503 && /SupportNotEnabled/i.test(code)) {
        this.$message.warning(
          "El asistente está temporalmente apagado. Te derivamos con soporte humano."
        );
        this.escalateNow();
        return;
      }
      if (status === 500 && /NoOpenAIKey|NoAIConfig/i.test(code)) {
        this.$message.error(
          "El asistente no está configurado. Avisale al administrador."
        );
        return;
      }
      if (status === 404 && /ConversationNotFound/i.test(code)) {
        // La convo del localStorage ya no existe — reset silencioso.
        this.reopen();
        this.$message.info("La conversación expiró. Empezamos una nueva.");
        return;
      }
      if (status === 429) {
        this.$message.warning(
          "Llegaste al límite de mensajes por hoy. Probá mañana o pedí soporte humano."
        );
        return;
      }
      const generic =
        code || "No pude mandar el mensaje. Reintentá en un momento.";
      this.$message.error(generic);
    },
    /**
     * @description Usuario fuerza escalación desde la card.
     */
    async escalateNow() {
      try {
        await supportService.escalate(this.conversationId, "user_requested");
        this.status = "waiting_human";
        this.messages.push({
          _id: `local-${Date.now()}`,
          sender: "system",
          text: "Te derivamos con soporte humano. Te respondemos lo antes posible.",
          sentAt: new Date(),
        });
        this.scrollDown();
      } catch (err) {
        this.$message.error("No pude derivar a soporte. Reintentá.");
      }
    },
    /**
     * @description Manda CSAT al backend. Si es 👎, muestra card de derivación.
     * @param {string} rating
     */
    async sendFeedback(rating) {
      try {
        await supportService.rateConversation(this.conversationId, rating);
        this.hasFeedback = true;
        if (rating === "negative") {
          this.negativeAt = new Date();
          this.scrollDown();
        } else {
          this.$message.success("¡Gracias!");
        }
      } catch (err) {
        this.$message.error("No pude registrar el feedback.");
      }
    },
    /**
     * @description Decide si mostrar los chips de feedback debajo de un mensaje.
     *              Solo aparecen una vez (debajo del ÚLTIMO mensaje del bot)
     *              y si todavía no votó.
     * @param {Object} m
     * @returns {boolean}
     */
    shouldShowFeedbackAfter(m) {
      if (this.hasFeedback) return false;
      if (m.sender !== "bot") return false;
      const idx = this.messages.indexOf(m);
      return idx === this.lastBotMessageIdx && this.lastBotMessageIdx > 0;
    },
    /**
     * @description Resetea para iniciar conversación nueva.
     */
    reopen() {
      this.conversationId = null;
      this.messages = [];
      this.status = "bot_handling";
      this.assignedTo = null;
      this.hasFeedback = false;
      this.negativeAt = null;
      this.lastMessageId = null;
      localStorage.removeItem("supportConversationId");
      this.stopPolling();
    },
    /**
     * @description Empieza polling cada 3s.
     */
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(this.pollNewMessages, 3000);
    },
    /**
     * @description Detiene polling.
     */
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    /**
     * @description Mergea mensajes nuevos en `this.messages` deduplicando
     *              por `_id`. Crítico para evitar duplicados cuando
     *              `sendMessage` está esperando la respuesta del LLM (5-15s)
     *              y el polling cada 3s ya trae el `userMsg` que después
     *              también va a venir en el response del POST. Sin dedupe,
     *              el user veía su mensaje 2 veces (UC-REG-114, reportado
     *              por Federico 2026-06-11). Aplica al polling y a cualquier
     *              push múltiple del response.
     * @param {Array<Object>} newMessages
     */
    mergeMessages(newMessages) {
      if (!newMessages || !newMessages.length) return;
      const existingIds = new Set(this.messages.map((m) => String(m._id)));
      const fresh = newMessages.filter(
        (m) => m && m._id && !existingIds.has(String(m._id))
      );
      if (fresh.length) this.messages.push(...fresh);
    },
    /**
     * @description Pide al backend mensajes nuevos.
     */
    async pollNewMessages() {
      if (!this.conversationId || !this.isOpen) return;
      try {
        const res = await supportService.pollSince(
          this.conversationId,
          this.lastMessageId
        );
        if (res.messages && res.messages.length) {
          this.mergeMessages(res.messages);
          this.lastMessageId = res.messages[res.messages.length - 1]._id;
          this.scrollDown();
        }
        if (res.status && res.status !== this.status) {
          this.status = res.status;
          this.assignedTo = res.assignedTo;
        }
      } catch (err) {
        /* silent */
      }
    },
    /**
     * @description Clase del mensaje según sender.
     * @param {Object} m
     * @returns {Object}
     */
    messageClass(m) {
      return {
        "is-user": m.sender === "user",
        "is-bot": m.sender === "bot",
        "is-human": m.sender === "human",
        "is-system": m.sender === "system",
      };
    },
    /**
     * @description Scroll al final del listado.
     */
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.messagesScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$navy: #002d5e;
$navy-soft: #eef1f6;
$teal: #05e5ba;
$text: #1a1f36;
$muted: #6b7294;
$bubble-bot: #f5f6f8;
$border: #e6e8ee;

.support-chat-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  font-family: "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont,
    sans-serif;
}

/* ─── FAB ─────────────────────────────────────────────────────── */

.support-fab {
  width: 56px;
  height: 56px;
  border: none;
  border-radius: 50%;
  background: $navy;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 45, 94, 0.25);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  padding: 0;

  &:hover {
    transform: scale(1.06);
    box-shadow: 0 10px 28px rgba(0, 45, 94, 0.32);
  }

  ::v-deep svg {
    width: 24px;
    height: 24px;
    fill: white;
  }
}

/* ─── Panel ───────────────────────────────────────────────────── */

.support-panel {
  width: 380px;
  max-height: 580px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: support-panel-in 0.18s ease;
}

@keyframes support-panel-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── Header ──────────────────────────────────────────────────── */

.support-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 14px;
  border-bottom: 1px solid $border;
}

.support-panel-head-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.support-panel-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: $teal;
  flex-shrink: 0;

  &.is-green {
    background: $teal;
  }
  &.is-orange {
    background: #ff9d3d;
  }
  &.is-blue {
    background: #3b82f6;
  }
  &.is-grey {
    background: #b9bcc7;
  }
}

.support-panel-head-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.support-panel-head-name {
  font-weight: 600;
  font-size: 14px;
  color: $text;
}

.support-panel-head-sub {
  font-size: 11px;
  color: $muted;
  margin-top: 2px;
}

.support-panel-close {
  border: none;
  background: transparent;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $muted;
  padding: 0;

  &:hover {
    background: $navy-soft;
  }

  ::v-deep svg {
    width: 16px;
    height: 16px;
    fill: $muted;
  }
}

/* ─── Thread ──────────────────────────────────────────────────── */

.support-panel-thread {
  flex: 1;
  overflow-y: auto;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: white;
}

.support-msg {
  display: flex;
  &.is-user {
    justify-content: flex-end;
  }
  &.is-bot,
  &.is-human,
  &.is-system {
    justify-content: flex-start;
  }
}

.support-msg-bubble {
  max-width: 78%;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.45;
  border-radius: 18px;
  word-wrap: break-word;
}

.support-msg.is-user .support-msg-bubble {
  background: $navy;
  color: white;
  border-bottom-right-radius: 6px;
}

.support-msg.is-bot .support-msg-bubble,
.support-msg.is-human .support-msg-bubble {
  background: $bubble-bot;
  color: $text;
  border-bottom-left-radius: 6px;
}

.support-msg.is-system .support-msg-bubble {
  background: transparent;
  color: $muted;
  font-style: italic;
  font-size: 12px;
  text-align: center;
  padding: 6px 12px;
  max-width: 100%;
}

.support-msg-text {
  white-space: pre-wrap;
}

/* ─── Typing dots ─────────────────────────────────────────────── */

.support-msg-bubble.is-typing {
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 12px 14px;

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: $muted;
    opacity: 0.6;
    animation: support-typing 1.2s infinite ease-in-out;

    &:nth-child(2) {
      animation-delay: 0.18s;
    }
    &:nth-child(3) {
      animation-delay: 0.36s;
    }
  }
}

@keyframes support-typing {
  0%,
  60%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

/* ─── Feedback chips ──────────────────────────────────────────── */

.support-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px 8px;
  font-size: 12px;
  color: $muted;
}

.support-feedback-label {
  color: $muted;
}

.support-feedback-chip {
  border: 1px solid $border;
  background: white;
  border-radius: 14px;
  padding: 4px 14px;
  font-size: 12px;
  color: $text;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;

  &:hover {
    background: $navy-soft;
    border-color: $navy;
  }
}

/* ─── Handoff card ────────────────────────────────────────────── */

.support-handoff-card {
  margin: 8px 0 4px;
  background: linear-gradient(
    180deg,
    rgba(5, 229, 186, 0.08),
    rgba(5, 229, 186, 0.03)
  );
  border: 1px solid rgba(5, 229, 186, 0.25);
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.support-handoff-text {
  font-size: 13px;
  color: $text;
  line-height: 1.4;
}

.support-handoff-btn {
  align-self: flex-start;
  border: none;
  background: $navy;
  color: white;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: #001b3a;
  }
}

/* ─── Input ───────────────────────────────────────────────────── */

.support-panel-input {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px 14px;
  border-top: 1px solid $border;
  background: white;
}

.support-panel-textarea {
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  color: $text;
  background: $bubble-bot;
  border-radius: 12px;
  padding: 10px 14px;
  max-height: 100px;
  line-height: 1.4;

  &::placeholder {
    color: $muted;
  }
  &:focus {
    background: white;
    box-shadow: 0 0 0 2px rgba(0, 45, 94, 0.12);
  }
}

.support-panel-send {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: $navy;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;

  &:disabled {
    background: $border;
    cursor: not-allowed;
    opacity: 0.6;
  }
  &:not(:disabled):hover {
    background: #001b3a;
  }

  ::v-deep svg {
    width: 16px;
    height: 16px;
    fill: white;
  }
}

/* ─── Cerrada ─────────────────────────────────────────────────── */

.support-panel-closed {
  padding: 16px;
  border-top: 1px solid $border;
  text-align: center;
}

.support-panel-reopen {
  border: 1px solid $border;
  background: white;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 13px;
  color: $text;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: $navy-soft;
  }
}
</style>
